/**
 * Digital SAT — Review Mode (read-only) + Attempts Archive.
 *
 * Route: /dsat/attempts                    → archive list
 * Route: /dsat/attempt/:attempt_id/review  → single attempt review
 *
 * Review Mode is strictly read-only: answers, score and questions come from
 * the immutable `review_payload` frozen at submission time. Tutor comments
 * live in a SEPARATE collection so the graded record is never mutated.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, X as XIcon, Flag, MessageSquare, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

export function DSATAttemptsArchivePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get('/api/exam/v2/me/attempts?exam_type=dsat&limit=100');
        setItems(r.data?.items || []);
      } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="dsat-attempts-archive">
      <div className="mb-6">
        <Link to="/resources/digital-sat" className="text-xs text-red-700 hover:underline">← Back to Digital SAT</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">My SAT Attempts</h1>
        <p className="text-xs text-gray-500 mt-1">Every attempt is archived permanently. Original answers and scores are never overwritten.</p>
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="p-2 text-left">Test</th>
              <th className="p-2">Type</th>
              <th className="p-2">Mode</th>
              <th className="p-2">Started</th>
              <th className="p-2">Score</th>
              <th className="p-2">Status</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.attempt_id} className="border-t border-gray-100" data-testid={`dsat-archive-row-${a.attempt_id}`}>
                <td className="p-2">{a.test_title}</td>
                <td className="p-2 text-center text-xs"><Badge variant="outline" className="text-[10px]">{a.test_type}</Badge></td>
                <td className="p-2 text-center text-xs"><Badge variant="outline" className="text-[10px]">{a.mode}</Badge></td>
                <td className="p-2 text-center text-xs text-gray-500">{(a.started_at || '').split('T')[0]}</td>
                <td className="p-2 text-center font-mono">{a.score ? `${a.score.percentage}%` : '—'}</td>
                <td className="p-2 text-center"><Badge className={`text-[10px] ${a.status === 'submitted' ? 'bg-green-600' : 'bg-gray-500'}`}>{a.status}</Badge></td>
                <td className="p-2 text-right">
                  <Link to={a.status === 'submitted' ? `/dsat/attempt/${a.attempt_id}/review` : `/dsat/attempt/${a.attempt_id}`} className="text-red-700 text-xs underline">
                    {a.status === 'submitted' ? 'Review' : 'Resume'}
                  </Link>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-gray-500">No attempts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DSATReviewPage() {
  const { attempt_id } = useParams();
  const { user } = useAuth();
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tutorComment, setTutorComment] = useState('');
  const [tutorPerQ, setTutorPerQ] = useState({});
  const isTutor = (user?.role || '').toLowerCase() === 'tutor' || (user?.role || '').toLowerCase() === 'admin';

  const load = async () => {
    try {
      const r = await ax.get(`/api/exam/v2/attempts/${attempt_id}`);
      setAttempt(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [attempt_id]);

  const addQuestionComment = async (qid) => {
    const comment = tutorPerQ[qid];
    if (!comment) return;
    try {
      await ax.post(`/api/exam/v2/attempts/${attempt_id}/review`, { question_id: qid, comment, flag_for_lesson: true });
      toast.success('Comment added');
      setTutorPerQ((p) => ({ ...p, [qid]: '' }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const addOverall = async () => {
    if (!tutorComment) return;
    try {
      await ax.post(`/api/exam/v2/attempts/${attempt_id}/review`, { comment: tutorComment, marked_reviewed: true });
      toast.success('Marked reviewed');
      setTutorComment('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!attempt) return null;
  const sc = attempt.score || {};
  const rp = attempt.review_payload || [];
  const commentsByQ = {};
  const overall = [];
  for (const r of attempt.reviews || []) {
    if (r.question_id) {
      commentsByQ[r.question_id] = commentsByQ[r.question_id] || [];
      commentsByQ[r.question_id].push(r);
    } else overall.push(r);
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="dsat-review-page">
      <div className="mb-6">
        <Link to="/dsat/attempts" className="text-xs text-red-700 hover:underline inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Attempts</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Review · {attempt.test_title}</h1>
        <p className="text-xs text-gray-500 mt-1">Read-only. Original answers and score are never modified by tutor comments.</p>
      </div>

      {/* Summary */}
      <Card className="p-5 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="dsat-review-summary">
        <div><div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Score</div><div className="text-2xl font-bold text-red-700">{sc.percentage ?? 0}%</div></div>
        <div><div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Correct</div><div className="text-2xl font-bold">{sc.raw_score ?? 0} / {sc.total_questions ?? 0}</div></div>
        <div><div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Mode</div><div className="text-sm font-semibold">{attempt.mode}</div></div>
        <div><div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Review status</div><div className="text-sm font-semibold">{attempt.review_status || 'not yet'}</div></div>
      </Card>

      {/* Per-domain breakdown */}
      {sc.domain_scores && Object.keys(sc.domain_scores).length > 0 && (
        <Card className="p-4 mb-6" data-testid="dsat-review-domains">
          <div className="text-xs font-bold text-gray-900 mb-2">Domain performance</div>
          <div className="grid sm:grid-cols-2 gap-1.5 text-xs">
            {Object.entries(sc.domain_scores).map(([d, v]) => (
              <div key={d} className="flex justify-between border-b border-gray-100 py-1">
                <span className="text-gray-600">{d}</span>
                <span className="font-mono">{v.correct}/{v.total}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Overall tutor block */}
      {(overall.length > 0 || isTutor) && (
        <Card className="p-4 mb-6 bg-red-50/40 border-red-100" data-testid="dsat-review-overall">
          <div className="text-xs font-bold text-gray-900 mb-2">Overall tutor feedback</div>
          {overall.map((r) => (
            <div key={r.id} className="text-sm text-gray-800 mb-2">
              <div className="text-[10px] text-gray-500">{r.tutor_name || r.tutor_email} · {(r.created_at || '').replace('T', ' ').slice(0, 16)}</div>
              {r.comment}
            </div>
          ))}
          {isTutor && (
            <div className="mt-2">
              <Textarea rows={2} value={tutorComment} onChange={(e) => setTutorComment(e.target.value)} placeholder="Overall feedback · mark as reviewed" data-testid="dsat-tutor-overall-input" />
              <div className="mt-2 flex justify-end"><Button size="sm" onClick={addOverall} className="bg-red-700 hover:bg-red-800 text-white" data-testid="dsat-tutor-overall-save">Save &amp; mark reviewed</Button></div>
            </div>
          )}
        </Card>
      )}

      {/* Questions */}
      <div className="space-y-4">
        {rp.map((q, i) => (
          <Card key={q.question_id} className={`p-5 ${q.is_correct ? 'border-green-200' : 'border-red-200'}`} data-testid={`dsat-review-q-${q.question_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Question {i + 1} · {q.section === 'rw' ? 'Reading &amp; Writing' : 'Math'} · {q.skill || q.domain}</div>
              {q.is_correct ? <Badge className="bg-green-600 text-[10px]"><Check className="h-3 w-3 mr-0.5" /> Correct</Badge> : <Badge variant="outline" className="border-red-300 text-red-700 text-[10px]"><XIcon className="h-3 w-3 mr-0.5" /> Incorrect</Badge>}
            </div>
            {q.passage && <div className="mt-3 p-3 rounded bg-gray-50 border border-gray-200 text-sm whitespace-pre-wrap">{q.passage}</div>}
            <div className="mt-3 text-sm text-gray-900 font-medium whitespace-pre-wrap">{q.prompt}</div>
            {q.options && (
              <div className="mt-3 space-y-1">
                {q.options.map((opt) => {
                  const isCorrect = opt.key === q.correct_answer;
                  const isStudent = opt.key === q.student_answer;
                  const cls = isCorrect ? 'border-green-500 bg-green-50' : (isStudent ? 'border-red-500 bg-red-50' : 'border-gray-200');
                  return (
                    <div key={opt.key} className={`p-2 rounded border text-xs ${cls}`}>
                      <span className="font-mono font-bold mr-2">{opt.key}</span>
                      {opt.text}
                      {isCorrect && <Badge className="ml-2 bg-green-600 text-[9px]">Correct</Badge>}
                      {isStudent && !isCorrect && <Badge variant="outline" className="ml-2 border-red-400 text-red-700 text-[9px]">Your answer</Badge>}
                    </div>
                  );
                })}
              </div>
            )}
            {q.question_type === 'spr' && (
              <div className="mt-3 text-xs">
                <div>Your answer: <span className="font-mono">{q.student_answer ?? '—'}</span></div>
                <div>Correct: <span className="font-mono font-bold">{q.correct_answer}</span></div>
              </div>
            )}
            {q.explanation && (
              <div className="mt-3 p-3 rounded bg-gray-50 text-xs text-gray-700">
                <div className="font-bold text-gray-900 mb-1">Explanation</div>{q.explanation}
                {q.trap_explanation && <div className="mt-2 text-gray-600 italic"><Flag className="h-3 w-3 inline mr-1" /> {q.trap_explanation}</div>}
              </div>
            )}
            {(commentsByQ[q.question_id] || []).map((r) => (
              <div key={r.id} className="mt-3 p-3 rounded border border-yellow-200 bg-yellow-50 text-xs" data-testid={`dsat-review-q-comment-${q.question_id}`}>
                <div className="font-semibold text-yellow-900 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {r.tutor_name || r.tutor_email}</div>
                <div className="text-gray-800 mt-0.5">{r.comment}</div>
              </div>
            ))}
            {isTutor && (
              <div className="mt-3 flex gap-2">
                <input
                  className="flex-1 h-8 px-2 rounded border border-gray-300 text-xs"
                  placeholder="Add a per-question comment"
                  value={tutorPerQ[q.question_id] || ''}
                  onChange={(e) => setTutorPerQ((p) => ({ ...p, [q.question_id]: e.target.value }))}
                  data-testid={`dsat-tutor-q-input-${q.question_id}`}
                />
                <Button size="sm" variant="outline" onClick={() => addQuestionComment(q.question_id)} data-testid={`dsat-tutor-q-save-${q.question_id}`}>Add</Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
